import { base64ToUtf8 } from './apiHooks/utils';

// GATSBY_ is important prefix for the environment variable to become available in the compiled JavaScript
const isEnterprise = (process.env.GATSBY_BIOLIB_IS_ENTERPRISE ?? '').toUpperCase() === 'TRUE';
const frontendVersion = process.env.GATSBY_BIOLIB_FRONTEND_VERSION ?? 'development';
const enterpriseLogoPath = '/api/system/enterprise/logo.png';

function parseEnterpriseReplaceVariable(value: string): string {
    if (!isEnterprise) {
        return '';
    }

    // During SSR (Gatsby build), window is undefined so we return the raw placeholder string
    // to be included in pre-built HTML for string replacement at container startup.
    // In the browser when JS files are loaded, the placeholder has been replaced with a
    // base64 encoded value by the nginx/string_replace.py script, so we decode it.
    if (typeof window === 'undefined') {
        return value;
    }
    try {
        return base64ToUtf8(value);
    } catch {
        return '';
    }
}

const buildInfo = `${isEnterprise ? 'Enterprise ' : ''}Build v${frontendVersion}`;
const isDev = frontendVersion === 'development';
const isBetaEnabled = typeof window !== 'undefined' && window.localStorage.getItem('beta') === 'true';
if (isBetaEnabled) {
    console.warn('Beta mode is enabled');
}

let baseURL: string;
let domainForCookies: string;
let githubSsoClientId: string | null = null;
let isTeamSubdomain = false;
let resultRendererHostname = '';

if (typeof location !== 'undefined') {
    baseURL = location.origin;
    domainForCookies = location.hostname;

    if (!isEnterprise || isDev) {
        if (isDev) {
            githubSsoClientId = 'ea0bf848d182728e85e9'; // Dev GitHub oauth app client id
            const hostnameParts = location.hostname.split('.');

            if (hostnameParts[hostnameParts.length - 1] === 'localhost' && hostnameParts.length > 1) {
                isTeamSubdomain = true;
            } else if (hostnameParts.length > 2) {
                isTeamSubdomain = true;
            }
        } else {
            const biolibComStagingHostname = 'biolib.io';
            const biolibComProductionHostname = 'biolib.com';

            if (location.hostname.endsWith(biolibComStagingHostname)) {
                domainForCookies = biolibComStagingHostname;
                githubSsoClientId = 'fb3b272bca90de557547'; // Staging GitHub oauth app client id
                // Staging stacks are hosted on `<stack prefix>.biolib.io` and serve results from
                // `results.<stack prefix>.biolibusercontent.com`
                const hostnameParts = location.hostname.split('.');
                const stackPrefix = hostnameParts[hostnameParts.length - 3];
                resultRendererHostname = stackPrefix ? `results.${stackPrefix}.biolibusercontent.com` : '';
                if (hostnameParts.length > 3) {
                    isTeamSubdomain = true;
                }
            } else if (location.hostname.endsWith(biolibComProductionHostname)) {
                domainForCookies = biolibComProductionHostname;
                githubSsoClientId = 'fe28c335660973b8ff74'; // Production GitHub oauth app client id
                resultRendererHostname = 'results.biolibusercontent.com';
                if (location.hostname !== biolibComProductionHostname) {
                    isTeamSubdomain = true;
                }
            } else {
                throw new Error(`Unrecognized hostname "${location.hostname}"`);
            }
        }
    }
} else {
    // We are in Gatsby build phase and need to set variables though they will be updated at runtime
    baseURL = '';
    domainForCookies = '';
}

function isTeamSubdomainPage(locationFromComponentProps?: Location): boolean {
    const teamSubdomainPages = ['/client-routes/team-subdomain/', '/event/'];
    if (isEnterprise || (!locationFromComponentProps && typeof window === 'undefined')) {
        return false;
    }
    const location = locationFromComponentProps ? locationFromComponentProps : window.location;
    return isTeamSubdomain || (location && teamSubdomainPages.some((page) => location.pathname.startsWith(page)));
}

enum localStorageKey {
    refreshToken = 'refreshToken',
    teamInvitation = 'teamInvitation',
}

if (isEnterprise) {
    resultRendererHostname = parseEnterpriseReplaceVariable('ENTERPRISE_REPLACE_RESULT_RENDERER_HOSTNAME');
}

const resourceHostnamePrefix = parseEnterpriseReplaceVariable('ENTERPRISE_REPLACE_RESOURCE_PREFIX');
const stackDescription = parseEnterpriseReplaceVariable('ENTERPRISE_REPLACE_STACK_DESCRIPTION');
const stackTitle = parseEnterpriseReplaceVariable('ENTERPRISE_REPLACE_STACK_TITLE');
const supportUrl = parseEnterpriseReplaceVariable('ENTERPRISE_REPLACE_SUPPORT_URL');

const resourcePrefix = !isEnterprise ? '' : `@${resourceHostnamePrefix}/`;
const allowedResourcePrefixes: string[] = [`%40${resourceHostnamePrefix}`, `@${resourceHostnamePrefix}`];

export default {
    allowedResourcePrefixes,
    baseURL,
    buildInfo,
    domainForCookies,
    enterpriseLogoPath,
    frontendVersion,
    githubSsoClientId,
    isBetaEnabled,
    isDev,
    isEnterprise,
    isTeamSubdomain,
    isTeamSubdomainPage,
    localStorageKey,
    resourceHostnamePrefix,
    resourcePrefix,
    resultRendererHostname,
    stackDescription,
    stackTitle,
    supportUrl,
    placeholderProfileImage: 'https://blbcdn.com/profile-pictures/default-profile-picture.png',
    biolibCommunitySlackInviteUrl:
        'https://join.slack.com/t/biolibcommunity/shared_invite/zt-k9m6xqbp-Gz84qaKL3Of8P7FRWQ3KIQ',
    githubSsoScope: 'user:email',
    itemsPerPage: 10,
    fileRendererHtmlPaths: {
        generic: process.env.GATSBY_BIOLIB_GENERIC_FILE_RENDERER_HTML_PATH,
        structure: process.env.GATSBY_BIOLIB_STRUCTURE_FILE_RENDERER_HTML_PATH,
    },
};
